import { useEffect, useState } from "react";
import moment from "moment";
// material
import { Card, Table, Stack, TableRow, TableBody, TableCell, Container, Typography, TableContainer, CircularProgress, Select, MenuItem, InputLabel, FormControl, Chip, Button } from "@mui/material";
// components
import Page from "../../components/Page";
import Scrollbar from "../../components/Scrollbar";
import SearchNotFound from "../../components/SearchNotFound";

import ListHead from "src/components/common/ListHead";
import MyPagination from "src/components/common/MyPagination";
import { asyncRequest } from "src/services/network";
import { alert } from "src/services/alert";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

const TABLE_HEAD = [
  { id: "id", label: "ID", alignRight: false },
  { id: "price", label: "单价", alignRight: false },
  { id: "totalContributionValue", label: "总贡献值", alignRight: false },
  { id: "totalFund", label: "总资金", alignRight: false },
  { id: "note", label: "处理类型", alignRight: false },
  { id: "relatedOrders", label: "关联订单", alignRight: false },
  { id: "recordTime", label: "记录时间", alignRight: false },
];

const NOTE_MAP = {
  "拼团定时任务批量处理更新": "已处理涨价",
  "拼团定时任务价格更新（无待处理记录）": "无待处理",
};

const RELATION_TYPE_OPTIONS = [
  { code: "order", label: "商城订单" },
  { code: "pintuan", label: "拼团中奖" },
  { code: "pt21", label: "21人团" },
  { code: "jyt-exchange", label: "推荐业绩+银豆兑换权益金" },
  { code: "jyt-release", label: "金银通释放" },
  { code: "free-benefit", label: "广告积分兑换" },
];

export default function ContributionPriceHistory() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);

  const [filter, setFilter] = useState({
    page: 0,
    size: 20,
    onlyProcessed: 0,
    relationType: 0,
    startDate: null,
    endDate: null,
  });

  useEffect(() => {
    setItems(null);
    setTotal(0);
    getItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  async function getItems() {
    const { data, count } = await asyncRequest({
      path: "/contribution-value/price-history",
      data: {
        page: filter.page,
        size: filter.size,
        onlyProcessed: filter.onlyProcessed ? 1 : 0,
        relationType: filter.relationType || "",
        startDate: filter.startDate ? filter.startDate.startOf("day").format("YYYY-MM-DD HH:mm:ss") : null,
        endDate: filter.endDate ? filter.endDate.endOf("day").format("YYYY-MM-DD HH:mm:ss") : null,
      },
    });

    setItems(data);
    setTotal(count);
  }

  const handlePageChange = (event, value, newSize) => {
    if (newSize !== undefined) {
      setFilter({ ...filter, page: 0, size: newSize });
      return;
    }
    const newPage = value - 1;
    if (newPage === filter.page) return;
    setFilter({ ...filter, page: newPage });
  };

  function handleReset() {
    setFilter({ page: 0, size: 20, onlyProcessed: 0, relationType: 0, startDate: null, endDate: null });
  }

  async function handleExport() {
    const { msg } = await asyncRequest({
      path: "/contribution-value/price-history/export",
      data: {
        onlyProcessed: filter.onlyProcessed ? 1 : 0,
        relationType: filter.relationType || "",
        startDate: filter.startDate ? filter.startDate.startOf("day").format("YYYY-MM-DD HH:mm:ss") : null,
        endDate: filter.endDate ? filter.endDate.endOf("day").format("YYYY-MM-DD HH:mm:ss") : null,
      },
    });
    alert(msg, "success");
  }

  const isUserNotFound = items && items.length === 0;

  return (
    <Page title="贡献值单价涨价记录">
      <Container maxWidth="2000px">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4" gutterBottom>
            贡献值单价涨价记录
          </Typography>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center" mb={3} flexWrap="wrap" rowGap={2}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>处理类型</InputLabel>
            <Select
              value={filter.onlyProcessed}
              label="处理类型"
              onChange={(e) => setFilter({ ...filter, page: 0, onlyProcessed: e.target.value })}
            >
              <MenuItem value={0}>全部</MenuItem>
              <MenuItem value={1}>处理了涨价的</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>订单类型</InputLabel>
            <Select
              value={filter.relationType}
              label="订单类型"
              onChange={(e) => setFilter({ ...filter, page: 0, relationType: e.target.value })}
            >
              <MenuItem value={0}>全部</MenuItem>
              {RELATION_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.code} value={opt.code}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="开始日期"
              slotProps={{ textField: { size: "small" } }}
              value={filter.startDate}
              onChange={(e) => setFilter({ ...filter, page: 0, startDate: e })}
            />
            <DatePicker
              label="结束日期"
              slotProps={{ textField: { size: "small" } }}
              value={filter.endDate}
              onChange={(e) => setFilter({ ...filter, page: 0, endDate: e })}
            />
          </LocalizationProvider>

          <Stack direction="row" spacing={1}>
            <Button variant="outlined" size="small" onClick={handleReset}>重置</Button>
            <Button variant="outlined" size="small" onClick={getItems}>刷新</Button>
            <Button variant="outlined" size="small" onClick={handleExport}>导出</Button>
          </Stack>
        </Stack>

        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <ListHead headLabel={TABLE_HEAD} />
                {!items ? (
                  <Stack sx={{ width: 500, height: 200 }} alignItems="center" justifyContent="center">
                    <CircularProgress />
                  </Stack>
                ) : (
                  <TableBody>
                    {items.map((row) => {
                      const { id, price, totalContributionValue, totalFund, note, recordTime, relatedOrders } = row;

                      return (
                        <TableRow hover key={id} tabIndex={-1}>
                          <TableCell align="left">{id}</TableCell>
                          <TableCell align="left">{price}</TableCell>
                          <TableCell align="left">{totalContributionValue}</TableCell>
                          <TableCell align="left">{totalFund}</TableCell>
                          <TableCell align="left">{NOTE_MAP[note] || note || "-"}</TableCell>
                          <TableCell align="left">
                            {relatedOrders && relatedOrders.length > 0 ? (
                              <Stack spacing={0.5}>
                                {relatedOrders.map((o, i) => (
                                  <Stack key={i} direction="row" spacing={0.5} alignItems="center">
                                    <Chip label={o.typeLabel} size="small" variant="outlined" />
                                    <Typography variant="body2">{o.sn}</Typography>
                                  </Stack>
                                ))}
                              </Stack>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell align="left">{recordTime ? moment(recordTime).format("YYYY-MM-DD HH:mm:ss") : "-"}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                )}

                {isUserNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={7} sx={{ py: 5 }}>
                        <SearchNotFound searchQuery="" />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <MyPagination total={total} page={filter.page} size={filter.size} onChange={handlePageChange} />
        </Card>
      </Container>
    </Page>
  );
}
